const VALID_STORAGE_PATH = /^[0-9a-fA-F-]{36}\//;

export const ASSET_URL_CACHE_TTL_MS = 45 * 60 * 1000;

export interface AssetUrlAsset {
  report_asset_id?: string | null;
  storage_path?: string | null;
}

export interface AssetUrlReport {
  day_report_id: string;
  assets?: AssetUrlAsset[] | null;
}

export interface ArchivedAssetLink {
  report_asset_id: string;
  url: string | null;
  preview_path?: string;
  storage_purged?: boolean;
  archive_error?: "archive_source_unavailable";
  source: "dropbox";
}

export interface SignedStorageUrl {
  path?: string | null;
  signedUrl?: string | null;
  error?: unknown;
}

export interface AssetUrlCacheEntry {
  url: string;
  expiresAt: number;
}

export interface AssetUrlResolverDependencies {
  fetchArchivedAssetLinks(dayReportId: string): Promise<ArchivedAssetLink[]>;
  signStorageUrls(paths: string[]): Promise<SignedStorageUrl[]>;
  onArchiveError?(error: unknown): void;
  onArchiveUnavailable?(path: string): void;
  now?(): number;
}

function archiveCacheKey(reportAssetId: string): string {
  return `dropbox/${reportAssetId}`;
}

function storageCacheKey(path: string): string {
  return `report-assets/${path}`;
}

function freshCachedUrl(
  cache: Map<string, AssetUrlCacheEntry>,
  key: string,
  now: number,
): string | null {
  const cached = cache.get(key);
  return cached && cached.expiresAt > now ? cached.url : null;
}

function cacheUrl(
  cache: Map<string, AssetUrlCacheEntry>,
  key: string,
  url: string,
  now: number,
): void {
  cache.set(key, { url, expiresAt: now + ASSET_URL_CACHE_TTL_MS });
}

export function validStoragePaths(report: AssetUrlReport): string[] {
  return Array.from(
    new Set(
      (report.assets ?? [])
        .map((asset) => asset?.storage_path)
        .filter((path): path is string =>
          typeof path === "string" && VALID_STORAGE_PATH.test(path)
        ),
    ),
  );
}

export async function resolveAssetUrls(
  report: AssetUrlReport,
  dependencies: AssetUrlResolverDependencies,
  cache: Map<string, AssetUrlCacheEntry> = new Map(),
): Promise<Map<string, string>> {
  const paths = validStoragePaths(report);
  const result = new Map<string, string>();
  if (!paths.length) return result;

  const now = dependencies.now?.() ?? Date.now();
  const assets = (report.assets ?? []).filter((asset) =>
    typeof asset?.report_asset_id === "string" &&
    typeof asset?.storage_path === "string" &&
    VALID_STORAGE_PATH.test(asset.storage_path)
  );

  const storageFallbackBlocked = new Set<string>();
  let needsArchiveRefresh = false;
  for (const asset of assets) {
    const assetId = asset.report_asset_id as string;
    const path = asset.storage_path as string;
    const cached = freshCachedUrl(cache, archiveCacheKey(assetId), now);
    if (cached) result.set(path, cached);
    else needsArchiveRefresh = true;
  }

  if (needsArchiveRefresh) {
    try {
      const links = await dependencies.fetchArchivedAssetLinks(report.day_report_id);
      for (const link of links) {
        if (
          link?.source !== "dropbox" ||
          typeof link.report_asset_id !== "string"
        ) continue;

        if (typeof link.url === "string" && link.url) {
          cacheUrl(cache, archiveCacheKey(link.report_asset_id), link.url, now);
          continue;
        }

        if (link.storage_purged === true) {
          const asset = assets.find((candidate) =>
            candidate.report_asset_id === link.report_asset_id
          );
          const path = asset?.storage_path;
          if (typeof path === "string") {
            storageFallbackBlocked.add(path);
            dependencies.onArchiveUnavailable?.(path);
          }
        }
      }
      for (const asset of assets) {
        const assetId = asset.report_asset_id as string;
        const path = asset.storage_path as string;
        const cached = freshCachedUrl(cache, archiveCacheKey(assetId), now);
        if (cached) result.set(path, cached);
      }
    } catch (error) {
      dependencies.onArchiveError?.(error);
    }
  }

  const missing = paths.filter((path) => {
    if (storageFallbackBlocked.has(path)) return false;
    if (result.has(path)) return false;
    const cached = freshCachedUrl(cache, storageCacheKey(path), now);
    if (cached) {
      result.set(path, cached);
      return false;
    }
    return true;
  });

  if (missing.length) {
    const signedUrls = await dependencies.signStorageUrls(missing);
    signedUrls.forEach((item, index) => {
      if (!item?.signedUrl || item.error) return;
      const path = item.path || missing[index];
      if (!path) return;
      cacheUrl(cache, storageCacheKey(path), item.signedUrl, now);
      result.set(path, item.signedUrl);
    });
  }

  return result;
}
